import { useEffect, useRef } from 'react';
import type { ProductListItem } from 'types/capi/shared';

import ProductCardImpressionEvent from '~/events/ProductCardImpressionEvent';
import useUser from '~/hooks/useUser';

const VIEWABILITY_THRESHOLD = 0.5;

type Options = {
  isBot: boolean;
  position?: number;
  product: ProductListItem;
  surface?: string;
};

function useProductCardImpression({
  isBot,
  position,
  product,
  surface
}: Options) {
  const { guestId } = useUser();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (isBot || hasTracked.current) return;

    const element = elementRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            hasTracked.current ||
            !entry.isIntersecting ||
            entry.intersectionRatio < VIEWABILITY_THRESHOLD
          ) {
            return;
          }

          hasTracked.current = true;

          new ProductCardImpressionEvent({
            guestId,
            isBot,
            position,
            product,
            surface
          }).track();

          observer.disconnect();
        });
      },
      { threshold: VIEWABILITY_THRESHOLD }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [guestId, isBot, position, product, surface]);

  return elementRef;
}

export default useProductCardImpression;
